import { useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { useAppleMotion } from '@/hooks/useAppleMotion';

interface AppleButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  children: ReactNode;
  variant?: 'accent' | 'outline' | 'ghost';
}

const VARIANT_CLASSES: Record<string, string> = {
  accent: 'bg-wayne-accent text-white hover:bg-wayne-accent-2',
  outline: 'bg-transparent border border-wayne-hairline text-wayne-ink-2 hover:bg-wayne-surface hover:border-wayne-hairline-2 hover:text-wayne-ink',
  ghost: 'bg-transparent text-wayne-ink-2 hover:text-wayne-ink hover:bg-wayne-surface-2',
};

export function AppleButton({ children, variant = 'accent', className = '', disabled, ...props }: AppleButtonProps): JSX.Element {
  const { style, handlers } = useAppleMotion();
  const [pressed, setPressed] = useState(false);

  const wrappedHandlers = disabled
    ? {}
    : {
        onPointerDown: (_e: React.PointerEvent<HTMLButtonElement>) => {
          setPressed(true);
          handlers.onPointerDown();
        },
        onPointerUp: (_e: React.PointerEvent<HTMLButtonElement>) => {
          setPressed(false);
          handlers.onPointerUp();
        },
        onPointerLeave: (_e: React.PointerEvent<HTMLButtonElement>) => {
          setPressed(false);
          handlers.onPointerLeave();
        },
      };

  const brightnessFilter = pressed && variant === 'accent' ? 'brightness(0.9)' : 'brightness(1)';

  const mergedStyle: React.CSSProperties | undefined = disabled
    ? undefined
    : {
        ...style,
        filter: brightnessFilter,
        transition: `${typeof style.transition === 'string' ? style.transition : ''}, filter 80ms linear`.replace(/^,\s*/, ''),
      };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className={[
        'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer',
        VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.accent,
        className,
      ].join(' ')}
      style={mergedStyle}
      {...wrappedHandlers}
      {...props}
    >
      {children}
    </button>
  );
}
